import useAxios from 'hooks/useAxios';

export const getGames = async (limit = 20, page = 1) => {
  const api = useAxios();
  try {
    return (
      await api.get('/game', {
        params: { limit: limit, page: page }
      })
    ).data;
  } catch {
    return {
      data: [],
      meta: {
        totalItems: 0,
        itemCount: 0,
        itemsPerPage: 0,
        totalPages: 0,
        currentPage: 0
      }
    };
  }
};

export const getGame = async (id, limit = 20, page = 1) => {
  const api = useAxios();
  try {
    return (
      await api.get(`/game/${id}`, {
        params: { limit: limit, page: page }
      })
    ).data;
  } catch {
    return {
      data: [],
      meta: {
        totalItems: 0,
        itemCount: 0,
        itemsPerPage: 0,
        totalPages: 0,
        currentPage: 0
      }
    };
  }
};

// specifically added to update Aethir settings and not change any other game settings
export const updateGameAethir = async (
  id,
  aethirId = null,
  serverIp = '',
  isAethirActive = false,
  aethirIdUS = null,
  isAethirUSActive = false
) => {
  const api = useAxios();

  const res = await api.patch(`/game/${id}`, {
    aethirId: aethirId ? aethirId : null,
    isAethirActive: isAethirActive,
    aethirIdUS: aethirIdUS ? aethirIdUS : null,
    isAethirUSActive: isAethirUSActive,
    serverIp
  });
  return res;
};

export const updateGame = async (
  id,
  percentTake,
  minCrash,
  maxCrash,
  minPlayers,
  maxPlayers,
  takeOnLose,
  takingCap,
  gameCategory,
  theoreticalRtp,
  maxExposure,
  trainEventEnabled,
  trainEventStartsAt,
  trainEventEndsAt
) => {
  const api = useAxios();
  const payload = {
    percentTake: Number(percentTake),
    minCrash: Number(minCrash),
    maxCrash: Number(maxCrash),
    minPlayers: Number(minPlayers),
    maxPlayers: Number(maxPlayers),
    takeOnLose: takeOnLose,
    takingCap: Number(takingCap)
  };

  // Add gameCategory if provided
  if (gameCategory) {
    payload.gameCategory = gameCategory;
  }

  // Add theoreticalRtp if provided (only for slot games)
  if (theoreticalRtp !== undefined && theoreticalRtp !== null && theoreticalRtp !== '') {
    payload.theoreticalRtp = Number(theoreticalRtp);
  }

  // Add maxExposure if provided (null clears it = unlimited)
  if (maxExposure !== undefined && maxExposure !== null && maxExposure !== '') {
    payload.maxExposure = Number(maxExposure);
  } else {
    payload.maxExposure = null;
  }

  if (trainEventEnabled !== undefined) {
    payload.trainEventEnabled = Boolean(trainEventEnabled);
    payload.trainEventStartsAt =
      trainEventStartsAt && payload.trainEventEnabled
        ? new Date(trainEventStartsAt).toISOString()
        : null;
    payload.trainEventEndsAt =
      trainEventEndsAt && payload.trainEventEnabled
        ? new Date(trainEventEndsAt).toISOString()
        : null;
  }

  return (await api.patch(`/game/${id}`, payload)).data;
};

export const getTrainEventStatus = async (id) => {
  const api = useAxios();
  try {
    return (await api.get(`/game/${id}/train-event-status`, { skipAuthRedirect: true })).data;
  } catch (err) {
    console.error(err);
    return null;
  }
};

export const addAvatar = async (gameId, name, image, position) => {
  const api = useAxios();
  try {
    const formData = new FormData();
    formData.append('gameId', gameId);
    formData.append('name', name);
    formData.append('image', image);
    formData.append('position', position);
    return await api.post(`/avatar`, formData);
  } catch {
    return {
      data: [],
      meta: {
        totalItems: 0,
        itemCount: 0,
        itemsPerPage: 0,
        totalPages: 0,
        currentPage: 0
      }
    };
  }
};

export const updateAvatar = async (id, gameId, name, image, position) => {
  const api = useAxios();
  try {
    const formData = new FormData();
    gameId && formData.append('gameId', gameId);
    name && formData.append('name', name);
    image !== undefined && formData.append('image', image);
    position && formData.append('position', position);
    return await api.patch(`/avatar/${id}`, formData);
  } catch {
    return {
      data: [],
      meta: {
        totalItems: 0,
        itemCount: 0,
        itemsPerPage: 0,
        totalPages: 0,
        currentPage: 0
      }
    };
  }
};

export const deleteAvatar = async (id) => {
  const api = useAxios();
  try {
    return await api.delete(`/avatar/${id}`);
  } catch {
    return {
      data: [],
      meta: {
        totalItems: 0,
        itemCount: 0,
        itemsPerPage: 0,
        totalPages: 0,
        currentPage: 0
      }
    };
  }
};

export const getPresets = async () => {
  const api = useAxios();
  try {
    return await api.get('/bet-limit/nh-presets');
  } catch {
    return [];
  }
};

export const addBetLimit = async (gameId, minBet, maxBet, singleBet) => {
  const api = useAxios();
  try {
    return await api.post(`/bet-limit`, {
      gameId: Number(gameId),
      minBet: Number(minBet),
      maxBet: Number(maxBet),
      singleBet: Number(singleBet)
    });
  } catch {
    return {
      data: [],
      meta: {
        totalItems: 0,
        itemCount: 0,
        itemsPerPage: 0,
        totalPages: 0,
        currentPage: 0
      }
    };
  }
};

export const updateBetLimit = async (id, dto) => {
  const api = useAxios();
  try {
    return await api.patch(`/bet-limit/${id}`, dto);
  } catch (err) {
    throw err;
  }
};

export const deleteBetLimit = async (id) => {
  const api = useAxios();
  try {
    return await api.delete(`/bet-limit/${id}`, {});
  } catch (err) {
    throw err;
  }
};

export const createAccessCodes = async (
  quantity,
  validityStart,
  validityEnd,
  gameId,
  betLimitId
) => {
  const api = useAxios();
  try {
    const payload = {
      quantity: Number(quantity),
      validityStart,
      validityEnd,
      gameId: Number(gameId)
    };
    if (betLimitId) {
      payload.betLimitId = betLimitId;
    }
    return await api.post(`/access-code/bulk`, payload);
  } catch (err) {
    return err;
  }
};

export const getNhRoomsForBetLimit = async (betLimitId) => {
  const api = useAxios();
  try {
    return (await api.get(`/nh-game/bet-limit/${betLimitId}/rooms`)).data;
  } catch (err) {
    console.error('Failed to fetch NH rooms for bet limit', err);
    return [];
  }
};

export const stopNhRoom = async (roomId) => {
  const api = useAxios();
  try {
    return await api.post(`/nh-game/stop/${roomId}`);
  } catch (err) {
    console.error('Failed to stop NH room', err);
    throw err;
  }
};

/** Check if backend is in dev mode (returns true/false) */
export const checkNhDevMode = async () => {
  const api = useAxios();
  try {
    const res = await api.get('/nh-game/dev-status');
    return res.data?.devMode === true;
  } catch {
    return false;
  }
};

/** Simulate a block being mined for a room */
export const simulateBlockMined = async (roomId, coin, amountBtc = null) => {
  const api = useAxios();
  const payload = { coin };
  if (amountBtc !== null && amountBtc !== undefined && amountBtc !== '') {
    payload.amountBtc = Number(amountBtc);
  }
  return (await api.post(`/nh-game/simulate-block/${roomId}`, payload)).data;
};

/** Get current exchange rates, native block rewards, and BTC equivalents */
export const getNhExchangeRates = async () => {
  const api = useAxios();
  try {
    return (await api.get('/nh-game/exchange-rates')).data;
  } catch {
    return null;
  }
};

export const startNhEngine = async (betLimitId) => {
  const api = useAxios();
  return (await api.post(`/nh-game/start-engine/${betLimitId}`)).data;
};

export const stopNhEngine = async (betLimitId) => {
  const api = useAxios();
  return (await api.post(`/nh-game/stop-engine/${betLimitId}`)).data;
};

export const getNhEngineStatus = async (betLimitId) => {
  const api = useAxios();
  try {
    return (await api.get(`/nh-game/engine-status/${betLimitId}`)).data;
  } catch {
    return { betLimitId, engines: [], hasActiveEngine: false };
  }
};

export const createTournament = async (
  betLimitId,
  name,
  startDayOfWeek,
  endDayOfWeek,
  startTime,
  endTime,
  firstPlacePrize,
  secondPlacePrize,
  thirdPlacePrize,
  currency,
  recurring,
  isDaily,
  requireAccessCode
) => {
  const api = useAxios();
  try {
    const payload = {
      betLimitId,
      name,
      startTime,
      endTime,
      firstPlacePrize,
      secondPlacePrize,
      thirdPlacePrize,
      currency,
      recurring,
      isDaily,
      requireAccessCode: !!requireAccessCode
    };

    // Only include day of week fields if they're provided (not required for daily tournaments)
    if (startDayOfWeek !== undefined) {
      payload.startDayOfWeek = startDayOfWeek;
    }
    if (endDayOfWeek !== undefined) {
      payload.endDayOfWeek = endDayOfWeek;
    }

    return await api.post(`/tournament`, payload);
  } catch (err) {
    return err;
  }
};

export const deleteTournament = async (id) => {
  const api = useAxios();
  try {
    return await api.delete(`/tournament/${id}`, {});
  } catch (err) {
    return err;
  }
};

export const getTournaments = async () => {
  const api = useAxios();
  try {
    return await api.get(`/tournament`, { skipAuthRedirect: true });
  } catch (err) {
    return err;
  }
};

export const getTournamentLeaderboard = async (tournamentId) => {
  const api = useAxios();
  try {
    return (await api.get(`/tournament/leaderboard/${tournamentId}`, {})).data;
  } catch (err) {
    return err;
  }
};

export const getTournamentLeaderboardEntries = async (leaderboardId) => {
  const api = useAxios();
  try {
    return (await api.get(`/tournament/leaderboard/entries/${leaderboardId}`, {})).data;
  } catch (err) {
    return err;
  }
};

export const getGameActiveTournaments = async (gameId) => {
  const api = useAxios();
  try {
    return await api.get(`/tournament/active-tournaments/${gameId}`, {});
  } catch (err) {
    return err;
  }
};

export const getTournament = async (id) => {
  const api = useAxios();
  try {
    return await api.get(`/tournament/${id}`, {});
  } catch (err) {
    return err;
  }
};

export const getCurrentTournamentLeaderboard = async (betLimitId) => {
  const api = useAxios();
  try {
    return await api.get(`/tournament/current-leaderboard/${betLimitId}`, {});
  } catch (err) {
    return err;
  }
};

export const exportLeaderboard = async (leaderboardId) => {
  const api = useAxios();
  try {
    return await api.post(
      `/tournament/export/${leaderboardId}`,
      {},
      {
        responseType: 'blob'
      }
    );
  } catch (err) {
    return err;
  }
};
